import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, Pencil, Info, Check } from "lucide-react";
import { AdminLista, AdminTabela } from "@/components/admin/admin-lista";
import { useToast } from "@/hooks/use-toast";
import { useFaixasAuditoria, useReplaceFaixasAuditoria, corDaNota } from "@/hooks/use-auditoria";

import { PageHeader } from "@/components/layout/page-header";
import { DataCard } from "@/components/data/data-card";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow,
  DataTableHead, DataTableCell, DataTableNum,
} from "@/components/data/data-table";
import { EmptyState } from "@/components/data/empty-state";
// ─── Helpers locais ───────────────────────────────────────────────────────────

function hojeISO(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

/** Espelha `arredNota` de server/lib/auditoria-calculo.ts. */
function arred2(v: number): number {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

function num(v: string | number | null | undefined): number {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
}

function normalizarDecimal(v: string): string {
  return String(v).trim().replace(",", ".");
}

function nota2(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toFixed(2).replace(".", ",");
}

function pct(v: string | number | null | undefined): string {
  return num(v).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + "%";
}

function mensagemErro(e: unknown, fallback = "Tente novamente."): string {
  const bruto = e instanceof Error ? e.message : String(e ?? "");
  const m = /^\d{3}:\s*([\s\S]*)$/.exec(bruto);
  const corpo = m ? m[1] : bruto;
  try {
    const j = JSON.parse(corpo);
    if (j && typeof j.error === "string") return j.error;
  } catch {
    /* corpo não é JSON — usa o texto cru */
  }
  return corpo || fallback;
}

/** Trackpad rolando a página com o campo focado decrementa o número — sempre desfoca no scroll. */
function semScroll(e: React.WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur();
}

// ─── Faixas ───────────────────────────────────────────────────────────────────

type FaixaLida = {
  id: string; vigenciaInicio: string; notaMin: string;
  percentualVariavel: string; percentualFixo: string; rotulo: string | null;
};
type FaixaForm = { key: number; notaMin: string; percentualVariavel: string; percentualFixo: string; rotulo: string };

/** Rótulo do intervalo: a faixa vale do piso até o piso seguinte menos um centésimo. */
function intervaloFaixa(pisosDesc: number[], i: number): string {
  const min = pisosDesc[i];
  if (i === 0) return `${nota2(min)} ou mais`;
  return `de ${nota2(min)} a ${nota2(arred2(pisosDesc[i - 1] - 0.01))}`;
}

function validarFaixas(linhas: FaixaForm[]): string | null {
  if (!linhas.length) return "Informe ao menos uma faixa.";
  const pisos: number[] = [];
  for (const l of linhas) {
    const min = Number(normalizarDecimal(l.notaMin));
    const variavel = Number(normalizarDecimal(l.percentualVariavel));
    const fixo = Number(normalizarDecimal(l.percentualFixo));
    if (l.notaMin.trim() === "" || isNaN(min) || min < 0 || min > 10) {
      return "Cada faixa precisa de um piso de nota entre 0 e 10.";
    }
    if (l.percentualVariavel.trim() === "" || isNaN(variavel) || variavel < 0 || variavel > 100) {
      return "% do variável precisa estar entre 0 e 100.";
    }
    if (l.percentualFixo.trim() === "" || isNaN(fixo) || fixo < 0 || fixo > 100) {
      return "% do fixo precisa estar entre 0 e 100.";
    }
    if (pisos.some(p => Math.abs(p - min) < 0.005)) {
      return "Há duas faixas com o mesmo piso. Cada piso pode aparecer uma única vez.";
    }
    pisos.push(min);
  }
  if (!pisos.some(p => p === 0)) {
    return "Precisa existir uma faixa começando em 0. Sem ela, uma nota baixa fica sem faixa nenhuma.";
  }
  return null;
}

function FaixasEditor() {
  const { toast } = useToast();
  const { data: faixasData, isLoading } = useFaixasAuditoria();
  const replace = useReplaceFaixasAuditoria();

  const faixas = useMemo<FaixaLida[]>(() => (faixasData ?? []) as FaixaLida[], [faixasData]);
  const ordenadas = useMemo(
    () => [...faixas].sort((a, b) => num(b.notaMin) - num(a.notaMin)),
    [faixas],
  );
  const pisosDesc = useMemo(() => ordenadas.map(f => num(f.notaMin)), [ordenadas]);
  const vigenciaAtual = ordenadas[0]?.vigenciaInicio ?? null;

  const [editando, setEditando] = useState(false);
  const [linhas, setLinhas] = useState<FaixaForm[]>([]);
  const [vigencia, setVigencia] = useState(hojeISO());
  const [proximaKey, setProximaKey] = useState(1);

  const abrirEditor = () => {
    const base = ordenadas.map((f, i) => ({
      key: i + 1,
      notaMin: String(num(f.notaMin)),
      percentualVariavel: String(num(f.percentualVariavel)),
      percentualFixo: String(num(f.percentualFixo)),
      rotulo: f.rotulo ?? "",
    }));
    const iniciais: FaixaForm[] = base.length
      ? base
      : [{ key: 1, notaMin: "0", percentualVariavel: "0", percentualFixo: "100", rotulo: "Abaixo do mínimo" }];
    setLinhas(iniciais);
    setProximaKey(iniciais.length + 1);
    setVigencia(vigenciaAtual ?? hojeISO());
    setEditando(true);
  };

  const atualizar = (key: number, patch: Partial<FaixaForm>) =>
    setLinhas(ls => ls.map(l => (l.key === key ? { ...l, ...patch } : l)));
  const adicionar = () => {
    setLinhas(ls => [...ls, { key: proximaKey, notaMin: "", percentualVariavel: "", percentualFixo: "100", rotulo: "" }]);
    setProximaKey(k => k + 1);
  };
  const remover = (key: number) => setLinhas(ls => ls.filter(l => l.key !== key));

  const salvar = async () => {
    const erro = validarFaixas(linhas);
    if (erro) {
      toast({ title: "Revise as faixas", description: erro, variant: "destructive" });
      return;
    }
    try {
      await replace.mutateAsync({
        vigenciaInicio: vigencia,
        faixas: linhas.map(l => ({
          notaMin: normalizarDecimal(l.notaMin),
          percentualVariavel: normalizarDecimal(l.percentualVariavel),
          percentualFixo: normalizarDecimal(l.percentualFixo),
          rotulo: l.rotulo.trim() || null,
        })),
      });
      toast({ title: "Faixas salvas!" });
      setEditando(false);
    } catch (e) {
      toast({ title: "Erro ao salvar faixas", description: mensagemErro(e), variant: "destructive" });
    }
  };

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  const linhasOrdenadasEditor = [...linhas].sort((a, b) => num(b.notaMin) - num(a.notaMin));
  const pisosEditor = linhasOrdenadasEditor.map(l => num(l.notaMin));

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <PageHeader
        titulo="Faixas de nota"
        descricao="Quanto a nota do setor ou da pessoa vale, em percentual — não em reais"
      />

      <Card className="border-muted">
        <CardContent className="p-3 flex gap-2">
          <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            Cada faixa guarda só o piso da nota e vale até o piso seguinte — não existe buraco nem
            sobreposição. Os dois percentuais são o que a pessoa <b>RECEBE</b> naquela nota (não quanto
            perde). <b>Estes percentuais não viram reais aqui</b> — quem aplica sobre o fixo e o variável
            de cada contrato é o módulo Financeiro.
          </p>
        </CardContent>
      </Card>

      {!editando ? (
        <>
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {vigenciaAtual ? `Vigência desde ${dataBR(vigenciaAtual)}` : "Nenhuma faixa cadastrada"}
            </p>
            <Button size="sm" variant="outline" onClick={abrirEditor}>
              <Pencil className="h-4 w-4 mr-1" />
              Editar faixas
            </Button>
          </div>

          {ordenadas.length === 0 ? (
            <EmptyState
              titulo="Nenhuma faixa cadastrada"
              descricao="Cadastre as faixas para publicar percentual a partir da nota."
              acao={
                <Button variant="outline" onClick={abrirEditor}>
                  <Pencil className="h-4 w-4 mr-1" />Editar faixas
                </Button>
              }
              testId="empty-faixas"
            />
          ) : (
            <>
              {/* Mobile: card por faixa. A tabela de 5 colunas só cabia em 390px
                  com rolagem lateral, e o que rolava pra fora era justamente o
                  percentual — o dado da tela. */}
              <AdminLista>
                {ordenadas.map((f, i) => (
                  <div key={f.id} className="rounded-xl border bg-card px-3 py-2.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className={`h-2.5 w-2.5 rounded-full bg-current shrink-0 ${corDaNota(f.notaMin)}`} />
                        <span className="font-medium text-sm whitespace-nowrap">{intervaloFaixa(pisosDesc, i)}</span>
                      </span>
                      <span className="text-xs text-muted-foreground truncate">{f.rotulo || "—"}</span>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-[11px] text-muted-foreground">% do variável</p>
                        <p className="font-mono text-xs tabular-nums">{pct(f.percentualVariavel)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] text-muted-foreground">% do fixo que recebe</p>
                        <p className="font-mono text-xs tabular-nums">
                          {pct(f.percentualFixo)}
                          {num(f.percentualFixo) < 100 && (
                            <span className="block text-[11px] font-sans text-muted-foreground">
                              = {pct(100 - num(f.percentualFixo))} de desconto
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </AdminLista>

              <AdminTabela>
                <DataCard meta={`${ordenadas.length} faixa(s)`}>
                  <DataTable colunas={COLUNAS_FAIXAS}>
                    <DataTableHeader>
                      <DataTableRow>
                        <DataTableHead>Faixa</DataTableHead>
                        <DataTableHead>Nota</DataTableHead>
                        <DataTableHead>Rótulo</DataTableHead>
                        <DataTableHead num>% do variável</DataTableHead>
                        <DataTableHead num>% do fixo que recebe</DataTableHead>
                      </DataTableRow>
                    </DataTableHeader>
                    <DataTableBody>
                      {ordenadas.map((f, i) => (
                        <DataTableRow key={f.id}>
                          {/* A cor da faixa é DADO — é o que o auditor vê no campo.
                              Não há `corHex` em `aud_faixa`: a cor é DERIVADA da nota
                              por `corDaNota()`, o mesmo helper que pinta a nota final
                              na lista de auditorias. Por isso classe, e não `style`:
                              não existe hex no banco para passar. */}
                          <DataTableCell>
                            <span className={`inline-block h-3 w-6 rounded-sm bg-current ${corDaNota(f.notaMin)}`} />
                          </DataTableCell>
                          <DataTableCell destaque className="whitespace-nowrap">{intervaloFaixa(pisosDesc, i)}</DataTableCell>
                          <DataTableCell className="text-muted-foreground truncate">{f.rotulo || "—"}</DataTableCell>
                          <DataTableNum>{pct(f.percentualVariavel)}</DataTableNum>
                          <DataTableNum>
                            {pct(f.percentualFixo)}
                            {num(f.percentualFixo) < 100 && (
                              <span className="block text-[11px] font-sans text-muted-foreground">
                                = {pct(100 - num(f.percentualFixo))} de desconto
                              </span>
                            )}
                          </DataTableNum>
                        </DataTableRow>
                      ))}
                    </DataTableBody>
                  </DataTable>
                </DataCard>
              </AdminTabela>
            </>
          )}
        </>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1 max-w-xs">
            <Label>Vigência das faixas</Label>
            <Input type="date" value={vigencia} onChange={e => setVigencia(e.target.value)} />
            <p className="text-xs text-muted-foreground">
              Salvar na mesma data substitui o conjunto atual. Uma data nova cria outro conjunto.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Faixas</Label>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={adicionar}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Faixa
              </Button>
            </div>
            {linhas.map(l => (
              <div key={l.key} className="rounded-md border p-2 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Piso da nota</Label>
                    <Input
                      type="number" step="0.1" min="0" max="10" inputMode="decimal"
                      value={l.notaMin} onWheel={semScroll}
                      onChange={e => atualizar(l.key, { notaMin: e.target.value })}
                    />
                  </div>
                  <Button
                    variant="ghost" size="icon" className="mt-5"
                    onClick={() => remover(l.key)} disabled={linhas.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">% do variável que recebe</Label>
                    <Input
                      type="number" step="0.01" min="0" max="100" inputMode="decimal"
                      value={l.percentualVariavel} onWheel={semScroll}
                      onChange={e => atualizar(l.key, { percentualVariavel: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">% do fixo que recebe</Label>
                    <Input
                      type="number" step="0.01" min="0" max="100" inputMode="decimal"
                      value={l.percentualFixo} onWheel={semScroll}
                      onChange={e => atualizar(l.key, { percentualFixo: e.target.value })}
                    />
                    {l.percentualFixo.trim() !== "" && !isNaN(num(l.percentualFixo)) && num(l.percentualFixo) < 100 && (
                      <p className="text-[11px] text-muted-foreground">
                        = {pct(100 - num(l.percentualFixo))} de desconto
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Rótulo <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                  <Input
                    value={l.rotulo}
                    onChange={e => atualizar(l.key, { rotulo: e.target.value })}
                    placeholder="Bom, Excelente, Crítico..."
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-md border bg-muted/40 p-3 space-y-1">
            <p className="text-xs font-medium">Como vai ficar</p>
            {linhasOrdenadasEditor.map((l, i) => (
              <p key={l.key} className="text-xs text-muted-foreground">
                {intervaloFaixa(pisosEditor, i)} → {pct(l.percentualVariavel)} do variável, {pct(l.percentualFixo)} do fixo
                {num(l.percentualFixo) < 100 ? ` (${pct(100 - num(l.percentualFixo))} de desconto)` : ""}
                {l.rotulo.trim() ? ` — ${l.rotulo.trim()}` : ""}
              </p>
            ))}
          </div>

          <Separator />

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setEditando(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={replace.isPending}>
              <Check className="h-4 w-4 mr-1" />
              Salvar todas as faixas
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const COLUNAS_FAIXAS = ["6rem", "9rem", null, "10rem", "13rem"];

export default function AuditoriaFaixas() {
  return <FaixasEditor />;
}
